// src/components/TranslationProjects.js

import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Button,
    Grid,
    Card,
    CardContent,
    LinearProgress,
    IconButton,
    Menu,
    MenuItem,
    Chip,
    Breadcrumbs,
    Link,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    ToggleButtonGroup,
    ToggleButton,
    Avatar,
    AvatarGroup,
    Tooltip,
    InputAdornment,
    Autocomplete,
} from '@mui/material';
import Flag from 'react-world-flags';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import TranslateIcon from '@mui/icons-material/Translate';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FolderIcon from '@mui/icons-material/Folder';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import SearchIcon from '@mui/icons-material/Search';
import { CircularProgress } from '@mui/material';

// Icons for word/segment counts
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import GrainIcon from '@mui/icons-material/Grain';

import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { logout } from '../slices/authSlice';
import isoLanguages from './isoLanguages';
import { getProjectStatus, statusColor, getProjectProgress } from '../utils/projectStatus';


const INITIAL_VISIBLE_COUNT = 9;
const LOAD_MORE_STEP = 9;

const getLanguageDetails = (code) =>
    isoLanguages.find((lang) => lang.code.toLowerCase() === code?.toLowerCase()) || {
        name: code,
        countryCode: 'UN',
    };

const getProjectLangCodes = (project) =>
    Array.isArray(project.targetLanguages)
        ? project.targetLanguages.map((l) => (typeof l === 'string' ? l : l.code))
        : [project.targetLanguage];

const getProjectLangNames = (project) =>
    getProjectLangCodes(project).map((c) => getLanguageDetails(c).name);

const getFirstProjectId = (project) =>
    Array.isArray(project.targetLanguages) && project.targetLanguages.length > 0
        ? project.targetLanguages[0].projectId || project.targetLanguages[0]
        : project._id;


const getInitial = (str) =>
    typeof str === 'string' && str.length > 0 ? str[0].toUpperCase() : '';

export default function TranslationProjects() {
    const [createdProjects, setCreatedProjects] = useState([]);
    const [sharedProjects, setSharedProjects] = useState([]);
    const [viewMode, setViewMode] = useState('card');
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState([]);
    const [visibleCreatedCount, setVisibleCreatedCount] = useState(INITIAL_VISIBLE_COUNT);
    const [visibleSharedCount, setVisibleSharedCount] = useState(INITIAL_VISIBLE_COUNT);
    const [tagFilter, setTagFilter] = useState([]);
    const [allUniqueTags, setAllUniqueTags] = useState([]);
    const token = useSelector((state) => state.auth.token);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [downloadingProject, setDownloadingProject] = useState(null); // projectId or null
    const [downloadingType, setDownloadingType] = useState(null); // 'original', 'translated', etc.


    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const response = await axios.get('/api/projects?summary=true', {
                    headers: { Authorization: `Bearer ${token}` },
                });

                // Sort createdProjects by descending createdAt:
                const created = (response.data.createdProjects || [])
                    .slice()
                    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

                // Likewise sort sharedProjects:
                const shared = (response.data.sharedProjects || [])
                    .slice()
                    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

                setCreatedProjects(created);
                setSharedProjects(shared);

                // Extract unique tags
                const tags = new Set();
                created.forEach(p => (p.tags || []).forEach(t => tags.add(t)));
                shared.forEach(p => (p.tags || []).forEach(t => tags.add(t)));
                setAllUniqueTags(Array.from(tags).sort());
            } catch (err) {
                if (err.response?.status === 401) {
                    dispatch(logout());
                    navigate('/login', { replace: true });
                } else {
                    console.error('Error fetching projects:', err);
                }
            }
        };

        if (token) {
            fetchProjects();
        }
    }, [token, navigate]);

    useEffect(() => {
        setVisibleCreatedCount(INITIAL_VISIBLE_COUNT);
    }, [search, statusFilter, tagFilter, createdProjects]);

    useEffect(() => {
        setVisibleSharedCount(INITIAL_VISIBLE_COUNT);
    }, [sharedProjects]);

    const handleDownload = async (projectId, fileType = 'translated') => {
        setDownloadingProject(projectId);         // <--- Specify which project is downloading
        setDownloadingType(fileType);            // (optional, for overlay message)
        try {
            const response = await axios.get(
                `/api/export/${projectId}/download?type=${fileType}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob',
                }
            );
            const contentDisposition = response.headers['content-disposition'];
            let fileName = `${projectId}-${fileType}`;
            if (contentDisposition) {
                const match = contentDisposition.match(/filename="?([^"]+)"?/);
                if (match) fileName = match[1];
            }
            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error downloading file:', err);
        } finally {
            setDownloadingProject(null);          // <--- Clear when done
            setDownloadingType(null);             // (optional)
        }
    };



    const handleExportAsXliff = async (projectId) => {
        try {
            const response = await axios.get(`/api/projects/${projectId}/xliff`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob',
            });
            const blob = new Blob([response.data], { type: 'application/xml' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${projectId}.xliff`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error exporting XLIFF:', err);
        }
    };

    const handleDeleteProject = async (projectId) => {
        if (!window.confirm('Are you sure you want to delete this project?')) return;
        try {
            await axios.delete(`/api/projects/${projectId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setCreatedProjects((prev) => prev.filter((p) => p._id !== projectId));
            setSharedProjects((prev) => prev.filter((p) => p._id !== projectId));
        } catch (err) {
            console.error('Error deleting project:', err);
        }
    };

    const filterProjects = (projects) =>
        projects.filter((project) => {
            const projectName = (project.projectName || '').toLowerCase();
            const status = getProjectStatus(project);
            const sourceLang = getLanguageDetails(project.sourceLanguage).name.toLowerCase();
            const targetNames = getProjectLangNames(project).join(' ').toLowerCase();

            const matchesSearch =
                projectName.includes(search.toLowerCase()) ||
                status.includes(search.toLowerCase()) ||
                sourceLang.includes(search.toLowerCase()) ||
                targetNames.includes(search.toLowerCase());

            const matchesStatus = statusFilter.length === 0 || statusFilter.includes(status);

            const matchesTags = tagFilter.length === 0 || (project.tags && tagFilter.every(tag => project.tags.includes(tag)));

            return matchesSearch && matchesStatus && matchesTags;
        });

    const toggleStatus = (statusKey) => {
        setStatusFilter((prev) =>
            prev.includes(statusKey) ? prev.filter((s) => s !== statusKey) : [...prev, statusKey]
        );
    };

    const handleViewToggle = (_, nextView) => {
        if (nextView) setViewMode(nextView);
    };

    const filteredCreatedProjects = filterProjects(createdProjects);
    const displayedCreatedProjects = filteredCreatedProjects.slice(0, visibleCreatedCount);
    const displayedSharedProjects = sharedProjects.slice(0, visibleSharedCount);

    const handleShowMoreCreated = () => {
        setVisibleCreatedCount((prev) => prev + LOAD_MORE_STEP);
    };

    const handleShowMoreShared = () => {
        setVisibleSharedCount((prev) => prev + LOAD_MORE_STEP);
    };

    const breadcrumbs = location.pathname
        .split('/')
        .filter((x) => x)
        .map((path, idx, arr) => (
            <Link
                key={path}
                color={idx === arr.length - 1 ? 'text.primary' : 'inherit'}
                onClick={() => navigate(`/${arr.slice(0, idx + 1).join('/')}`)}
                sx={{ cursor: 'pointer', textTransform: 'capitalize' }}
            >
                {path}
            </Link>
        ));

    const ProjectCard = ({ project }) => {
        const [anchorEl, setAnchorEl] = useState(null);
        const open = Boolean(anchorEl);

        const handleOpen = (e) => {
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
        };
        const handleClose = () => {
            setAnchorEl(null);
        };

        const progress = getProjectProgress(project);
        const isFullyTranslated = progress >= 100;

        const doEdit = () => {
            navigate(`/dashboard/projects/${project._id}/edit`);
            handleClose();
        };
        const doDownloadOriginal = () => {
            handleDownload(getFirstProjectId(project), 'original');
            handleClose();
        };
        const doDownloadTranslated = () => {
            if (!isFullyTranslated) {
                handleClose();
                return;
            }
            handleDownload(getFirstProjectId(project), 'translated');
            handleClose();
        };
        const doExportXliff = () => {
            handleExportAsXliff(getFirstProjectId(project));
            handleClose();
        };
        const doDelete = () => {
            handleDeleteProject(project._id);
            handleClose();
        };

        // Compute segment count and word count
        const sections = Array.isArray(project.sections) ? project.sections : [];
        const segmentCount = typeof project.segmentCount === 'number'
            ? project.segmentCount
            : sections.length;

        const computedWordCount = typeof project.wordCount === 'number'
            ? project.wordCount
            : sections.reduce((sum, sec) => {
                const words = sec.split(/\s+/).filter(Boolean).length;
                return sum + words;
            }, 0);

        return (
            <Card
                sx={{
                    borderRadius: 3,
                    boxShadow: 3,
                    cursor: 'pointer',
                    position: 'relative',
                    transition: '0.2s',
                    '&:hover': { boxShadow: 8, transform: 'translateY(-2px)' },
                    mb: 3,
                }}
                onClick={() => navigate(`/dashboard/projects/${project._id}`)}
            >
                <CardContent sx={{ position: 'relative' }}>
                    {downloadingProject && downloadingProject === project._id && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0, left: 0, right: 0, bottom: 0,
                                bgcolor: 'rgba(255,255,255,0.85)',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 10,
                            }}
                        >
                            <CircularProgress size={42} />
                            <Typography sx={{ mt: 2 }} variant="subtitle1" color="text.secondary">
                                Preparing {downloadingType === 'original' ? 'original' : 'translated'} file...
                            </Typography>
                        </Box>
                    )}


                    {project.creationSource === 'slack' && (
                        <Tooltip title="Created via Slack">
                            <Box sx={{ position: 'absolute', top: 12, right: 48, zIndex: 2 }}>
                                <img src="/assets/logo/slack-logo.svg" alt="Slack Logo" width="20" height="20" />
                            </Box>
                        </Tooltip>
                    )}

                    {project.creationSource === 'figma' && (
                        <Tooltip title="Created via Figma">
                            <Box sx={{ position: 'absolute', top: 12, right: 48, zIndex: 2 }}>
                                <svg width="20" height="20" viewBox="0 0 15 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M3.75 22.5C5.82107 22.5 7.5 20.8211 7.5 18.75V11.25H3.75C1.67893 11.25 0 12.9289 0 15C0 17.0711 1.67893 18.75 3.75 18.75V22.5Z" fill="#0ACF83" />
                                    <path d="M0 7.5C0 5.42893 1.67893 3.75 3.75 3.75H7.5V11.25H3.75C1.67893 11.25 0 9.57107 0 7.5Z" fill="#A259FF" />
                                    <path d="M3.75 0C1.67893 0 0 1.67893 0 3.75H7.5V0H3.75Z" fill="#F24E1E" />
                                    <path d="M7.5 0H11.25C13.3211 0 15 1.67893 15 3.75C15 5.82107 13.3211 7.5 11.25 7.5H7.5V0Z" fill="#FF7262" />
                                    <path d="M15 11.25C15 13.3211 13.3211 15 11.25 15C9.17893 15 7.5 13.3211 7.5 11.25V7.5H11.25C13.3211 7.5 15 9.17893 15 11.25Z" fill="#1ABCFE" />
                                </svg>
                            </Box>
                        </Tooltip>
                    )}

                    <IconButton
                        size="small"
                        sx={{ position: 'absolute', top: 12, right: 12, zIndex: 2 }}
                        onClick={handleOpen}
                    >
                        <MoreVertIcon />
                    </IconButton>

                    <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <MenuItem onClick={doEdit}>
                            <i className="mdi mdi-pencil me-1"></i>Edit
                        </MenuItem>
                        <MenuItem
                            onClick={() => {
                                navigate(`/dashboard/projects/${getFirstProjectId(project)}/translate`);
                                handleClose();
                            }}>
                            <TranslateIcon fontSize="small" sx={{ mr: 1 }} />Translate
                        </MenuItem>
                        <MenuItem
                            onClick={() => {
                                setDownloadingProject(project._id);
                                handleDownload(project._id, 'original');
                                handleClose();
                            }}>
                            <i className="mdi mdi-download me-1"></i>Download Original
                        </MenuItem>
                        <MenuItem
                            onClick={() => {
                                if (!isFullyTranslated) {
                                    handleClose();
                                    return;
                                }
                                setDownloadingProject(project._id);
                                handleDownload(project._id, 'translated');
                                handleClose();
                            }}
                            disabled={!isFullyTranslated}
                        >
                            <i className="mdi mdi-download me-1"></i>Download Translated
                        </MenuItem>

                        <MenuItem onClick={doExportXliff}>
                            <i className="mdi mdi-file-export me-1"></i>Export as XLIFF
                        </MenuItem>
                        <MenuItem onClick={doDelete}>
                            <i className="mdi mdi-delete me-1"></i>Delete
                        </MenuItem>
                    </Menu>

                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <FolderIcon color="primary" sx={{ fontSize: 28 }} />
                        <Typography variant="h6" fontWeight={700}>
                            {project.projectName}
                        </Typography>
                    </Box>

                    <Box display="flex" gap={2} mt={1} mb={1}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <Flag
                                code={getLanguageDetails(project.sourceLanguage).countryCode}
                                style={{ width: 22 }}
                            />
                            <Typography variant="body2" color="text.secondary">
                                {getLanguageDetails(project.sourceLanguage).name}
                            </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                            <Flag
                                code={getLanguageDetails(getProjectLangCodes(project)[0]).countryCode}
                                style={{ width: 22 }}
                            />
                            <Typography variant="body2" color="text.secondary">
                                {getProjectLangNames(project).join(', ')}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Only Word / Segment counts (fontSize 12px) */}
                    <Box mb={2} display="flex" alignItems="center" gap={3}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <LibraryBooksIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 16 }}>
                                Words {computedWordCount}
                            </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <GrainIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 16 }}>
                                Segments {segmentCount}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Tags */}
                    {project.tags && project.tags.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                            {project.tags.map((tag, idx) => (
                                <Chip
                                    key={idx}
                                    label={tag}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                        height: 20,
                                        fontSize: '0.65rem',
                                        bgcolor: 'action.hover',
                                        borderColor: 'divider'
                                    }}
                                />
                            ))}
                        </Box>
                    )}

                    <Box display="flex" alignItems="center" gap={1} mt={2} mb={2}>
                        {project.createdBy && (
                            <Tooltip
                                title={`${project.createdBy.firstName || ''} ${project.createdBy.lastName || ''}`.trim()}
                                arrow
                            >
                                <Avatar
                                    src={
                                        project.createdBy.avatarUrl
                                            ? `/api/users/avatars/${project.createdBy._id}/${project.createdBy.avatarUrl
                                                .split('/')
                                                .pop()}`
                                            : ''
                                    }
                                    alt={`${project.createdBy.firstName || ''} ${project.createdBy.lastName || ''
                                        }`.trim()}
                                    sx={{ width: 30, height: 30, border: '2px solid #fff' }}
                                >
                                    {!project.createdBy.avatarUrl &&
                                        (getInitial(project.createdBy.firstName) +
                                            getInitial(project.createdBy.lastName))}
                                </Avatar>
                            </Tooltip>
                        )}

                        {Array.isArray(project.assignedTo) && project.assignedTo.length > 0 && (
                            <AvatarGroup sx={{ '& .MuiAvatar-root': { border: '2px solid #fff' } }} max={4}>
                                {project.assignedTo.map((translator) => (
                                    <Tooltip
                                        key={translator._id}
                                        title={`${translator.firstName || ''} ${translator.lastName || ''
                                            }`.trim()}
                                        arrow
                                    >
                                        <Avatar
                                            src={
                                                translator.avatarUrl
                                                    ? `/api/users/avatars/${translator._id}/${translator.avatarUrl
                                                        .split('/')
                                                        .pop()}`
                                                    : ''
                                            }
                                            alt={`${translator.firstName || ''} ${translator.lastName || ''}`.trim()}
                                            sx={{ width: 30, height: 30 }}
                                        >
                                            {!translator.avatarUrl &&
                                                (getInitial(translator.firstName) +
                                                    getInitial(translator.lastName))}
                                        </Avatar>
                                    </Tooltip>
                                ))}
                            </AvatarGroup>
                        )}
                    </Box>

                    <Box mt={1}>
                        <LinearProgress
                            variant="determinate"
                            value={progress}
                            sx={{ borderRadius: 2, height: 8 }}
                        />
                        <Box mt={0.5} display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="caption">{progress}% Complete</Typography>
                            <Chip
                                size="small"
                                label={getProjectStatus(project)}
                                color={statusColor[getProjectStatus(project)]}
                                sx={{ fontWeight: 700, textTransform: 'capitalize' }}
                            />
                        </Box>
                    </Box>

                    <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                            {project.deadline ? `Due: ${new Date(project.deadline).toLocaleDateString()}` : ''}
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    };

    function ListRowMenu({ project }) {
        const [anchorEl, setAnchorEl] = useState(null);
        const open = Boolean(anchorEl);

        const progress = getProjectProgress(project);
        const isFullyTranslated = progress >= 100;

        const handleOpen = (e) => {
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
        };
        const handleClose = () => {
            setAnchorEl(null);
        };

        const doDownloadOriginal = () => {
            handleDownload(getFirstProjectId(project), 'original');
            handleClose();
        };
        const doDownloadTranslated = () => {
            if (!isFullyTranslated) {
                handleClose();
                return;
            }
            handleDownload(getFirstProjectId(project), 'translated');
            handleClose();
        };
        const doExportXliff = () => {
            handleExportAsXliff(getFirstProjectId(project));
            handleClose();
        };
        const doDelete = () => {
            handleDeleteProject(project._id);
            handleClose();
        };

        return (
            <>
                <IconButton size="small" onClick={handleOpen} color="primary">
                    <CloudDownloadIcon />
                </IconButton>
                <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <MenuItem onClick={doDownloadOriginal}>
                        <i className="mdi mdi-download me-1"></i>Download Original
                    </MenuItem>
                    <MenuItem onClick={doDownloadTranslated} disabled={!isFullyTranslated}>
                        <i className="mdi mdi-download me-1"></i>Download Translated
                    </MenuItem>
                    <MenuItem onClick={() => { navigate(`/dashboard/projects/${getFirstProjectId(project)}/translate`); handleClose(); }}>
                        <TranslateIcon fontSize="small" sx={{ mr: 1 }} />Translate
                    </MenuItem>
                    <MenuItem onClick={doExportXliff}>
                        <i className="mdi mdi-file-export me-1"></i>Export as XLIFF
                    </MenuItem>
                    <MenuItem onClick={doDelete}>
                        <i className="mdi mdi-delete me-1"></i>Delete Project
                    </MenuItem>
                </Menu>
            </>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 5 }}>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
                <Link
                    color="inherit"
                    onClick={() => navigate('/dashboard/projects')}
                    sx={{ cursor: 'pointer' }}
                >
                    <FolderIcon sx={{ mr: 0.5 }} fontSize="inherit" /> Projects
                </Link>
                {breadcrumbs}
            </Breadcrumbs>

            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                <Typography variant="h4" fontWeight={800}>
                    Translation Projects
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                    <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Search by name, status, language..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: <SearchIcon sx={{ mr: 1 }} />,
                        }}
                        sx={{ minWidth: 260, mr: 2 }}
                    />
                    <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={handleViewToggle}
                        size="small"
                        sx={{
                            background: '#f2f5fa',
                            borderRadius: 2,
                            boxShadow: 1,
                        }}
                    >
                        <ToggleButton value="card">
                            <ViewModuleIcon />
                        </ToggleButton>
                        <ToggleButton value="list">
                            <ViewListIcon />
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <Button variant="contained" onClick={() => navigate('/dashboard/new-translation')}>
                        Create New Project
                    </Button>
                </Box>
            </Box>

            <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Autocomplete
                    multiple
                    size="small"
                    options={allUniqueTags}
                    value={tagFilter}
                    onChange={(e, newValue) => setTagFilter(newValue)}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="Filter by tags"
                            variant="outlined"
                            sx={{ minWidth: 200 }}
                        />
                    )}
                    sx={{ flexGrow: 1 }}
                />

                {/* Status‐Filter Chips */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {['in progress', 'translated', 'complete'].map((statusKey) => {
                        const isSelected = statusFilter.includes(statusKey);
                        const alwaysFilled = statusKey === 'in progress';

                        return (
                            <Chip
                                key={statusKey}
                                label={statusKey.charAt(0).toUpperCase() + statusKey.slice(1)}
                                size="small"
                                color={statusColor[statusKey]}
                                clickable
                                onClick={() => toggleStatus(statusKey)}
                                variant={
                                    alwaysFilled
                                        ? 'filled'
                                        : isSelected
                                            ? 'filled'
                                            : 'outlined'
                                }
                                sx={{
                                    textTransform: 'capitalize',
                                    height: 24,
                                    fontSize: '0.75rem',
                                }}
                            />
                        );
                    })}
                </Box>
            </Box>

            {viewMode === 'card' && (
                <>
                    <Typography variant="h5" sx={{ mb: 2 }}>
                        Projects You Created
                    </Typography>
                    {filteredCreatedProjects.length > 0 ? (
                        <>
                            <Grid container spacing={3}>
                                {displayedCreatedProjects.map((project) => (
                                    <Grid item xs={12} sm={6} md={4} key={project._id}>
                                        <ProjectCard project={project} />
                                    </Grid>
                                ))}
                            </Grid>
                            {filteredCreatedProjects.length > visibleCreatedCount && (
                                <Box display="flex" justifyContent="center" mt={3}>
                                    <Button variant="outlined" onClick={handleShowMoreCreated}>
                                        Show More
                                    </Button>
                                </Box>
                            )}
                        </>
                    ) : (
                        <Box textAlign="center" sx={{ mt: 4 }}>
                            <Typography variant="h6">No projects found.</Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => navigate('/dashboard/projects/new-translation')}
                            >
                                Create New Project
                            </Button>
                        </Box>
                    )}
                </>
            )}

            {viewMode === 'list' && (
                <>
                    <Typography variant="h5" sx={{ mb: 2 }}>
                        Projects You Created
                    </Typography>
                    {filteredCreatedProjects.length > 0 ? (
                        <>
                            <TableContainer component={Paper}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Project Name</TableCell>
                                            <TableCell>Source</TableCell>
                                            <TableCell>Target</TableCell>
                                            <TableCell>Progress</TableCell>
                                            <TableCell>Deadline</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {displayedCreatedProjects.map((project) => {
                                            // Compute progress based on translatedSections
                                            const progress = getProjectProgress(project);

                                            const sections = Array.isArray(project.sections) ? project.sections : [];
                                            const segmentCount = typeof project.segmentCount === 'number'
                                                ? project.segmentCount
                                                : sections.length;

                                            const computedWordCount = typeof project.wordCount === 'number'
                                                ? project.wordCount
                                                : sections.reduce((sum, sec) => {
                                                    const words = sec.split(/\s+/).filter(Boolean).length;
                                                    return sum + words;
                                                }, 0);

                                            return (
                                                <TableRow
                                                    key={project._id}
                                                    hover
                                                    sx={{ cursor: 'pointer' }}
                                                    onClick={() => navigate(`/dashboard/projects/${project._id}`)}
                                                >
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                            {project.projectName}
                                                            {project.tags && project.tags.length > 0 && (
                                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                                    {project.tags.map((tag, idx) => (
                                                                        <Chip
                                                                            key={idx}
                                                                            label={tag}
                                                                            size="small"
                                                                            variant="outlined"
                                                                            sx={{ height: 16, fontSize: '0.6rem' }}
                                                                        />
                                                                    ))}
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Flag
                                                            code={getLanguageDetails(project.sourceLanguage).countryCode}
                                                            style={{ width: 20, marginRight: 8 }}
                                                        />
                                                        {getLanguageDetails(project.sourceLanguage).name}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Flag
                                                            code={getLanguageDetails(getProjectLangCodes(project)[0]).countryCode}
                                                            style={{ width: 20, marginRight: 8 }}
                                                        />
                                                        {getProjectLangNames(project).join(', ')}
                                                    </TableCell>
                                                    <TableCell>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={progress}
                                                            sx={{ borderRadius: 2, height: 8 }}
                                                        />
                                                        <Typography variant="body2">{progress}%</Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        {project.deadline
                                                            ? new Date(project.deadline).toLocaleDateString()
                                                            : 'N/A'}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            size="small"
                                                            label={getProjectStatus(project)}
                                                            color={statusColor[getProjectStatus(project)]}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box display="flex" flexDirection="column" gap={0.5}>
                                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                                <LibraryBooksIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>
                                                                    {computedWordCount}
                                                                </Typography>
                                                            </Box>
                                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                                <GrainIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>
                                                                    {segmentCount}
                                                                </Typography>
                                                            </Box>
                                                            <IconButton
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    navigate(`/dashboard/projects/${getFirstProjectId(project)}/translate`);
                                                                }}
                                                                color="primary"
                                                            >
                                                                <TranslateIcon />
                                                            </IconButton>
                                                            <ListRowMenu project={project} />
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            {filteredCreatedProjects.length > visibleCreatedCount && (
                                <Box display="flex" justifyContent="center" mt={3}>
                                    <Button variant="outlined" onClick={handleShowMoreCreated}>
                                        Show More
                                    </Button>
                                </Box>
                            )}
                        </>
                    ) : (
                        <Box textAlign="center" sx={{ mt: 4 }}>
                            <Typography variant="h6">No projects found.</Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => navigate('/dashboard/projects/new-translation')}
                            >
                                Create New Project
                            </Button>
                        </Box>
                    )}
                </>
            )}

            <Typography variant="h5" sx={{ mt: 6, mb: 2 }}>
                Shared Projects
            </Typography>
            {sharedProjects.length > 0 ? (
                <>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Project Name</TableCell>
                                    <TableCell>Source</TableCell>
                                    <TableCell>Target</TableCell>
                                    <TableCell>Progress</TableCell>
                                    <TableCell>Deadline</TableCell>
                                    <TableCell>Created By</TableCell>
                                    <TableCell>Translate</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {displayedSharedProjects.map((project) => {
                                    // Compute progress based on translatedSections
                                    const progress = getProjectProgress(project);

                                    const sections = Array.isArray(project.sections) ? project.sections : [];
                                    const segmentCount = typeof project.segmentCount === 'number'
                                        ? project.segmentCount
                                        : sections.length;

                                    const computedWordCount = typeof project.wordCount === 'number'
                                        ? project.wordCount
                                        : sections.reduce((sum, sec) => {
                                            const words = sec.split(/\s+/).filter(Boolean).length;
                                            return sum + words;
                                        }, 0);

                                    return (
                                        <TableRow
                                            key={project._id}
                                            hover
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() => navigate(`/dashboard/projects/${project._id}`)}
                                        >
                                            <TableCell>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    {project.projectName}
                                                    {project.tags && project.tags.length > 0 && (
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                            {project.tags.map((tag, idx) => (
                                                                <Chip
                                                                    key={idx}
                                                                    label={tag}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    sx={{ height: 16, fontSize: '0.6rem' }}
                                                                />
                                                            ))}
                                                        </Box>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Flag
                                                    code={getLanguageDetails(project.sourceLanguage).countryCode}
                                                    style={{ width: 20, marginRight: 8 }}
                                                />
                                                {getLanguageDetails(project.sourceLanguage).name}
                                            </TableCell>
                                            <TableCell>
                                                <Flag
                                                    code={getLanguageDetails(getProjectLangCodes(project)[0]).countryCode}
                                                    style={{ width: 20, marginRight: 8 }}
                                                />
                                                {getProjectLangNames(project).join(', ')}
                                            </TableCell>
                                            <TableCell>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={progress}
                                                    sx={{ borderRadius: 2, height: 8 }}
                                                />
                                                <Typography variant="body2">{progress}%</Typography>
                                            </TableCell>
                                            <TableCell>
                                                {project.deadline
                                                    ? new Date(project.deadline).toLocaleDateString()
                                                    : 'N/A'}
                                            </TableCell>
                                            <TableCell>
                                                {project.createdBy && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        {project.createdBy.firstName} {project.createdBy.lastName}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Box display="flex" flexDirection="column" gap={0.5}>
                                                    <Box display="flex" alignItems="center" gap={0.5}>
                                                        <LibraryBooksIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>
                                                            {computedWordCount}
                                                        </Typography>
                                                    </Box>
                                                    <Box display="flex" alignItems="center" gap={0.5}>
                                                        <GrainIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>
                                                            {segmentCount}
                                                        </Typography>
                                                    </Box>
                                                    <IconButton
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            navigate(`/dashboard/projects/${getFirstProjectId(project)}/translate`);
                                                        }}
                                                        color="primary"
                                                    >
                                                        <TranslateIcon />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {sharedProjects.length > visibleSharedCount && (
                        <Box display="flex" justifyContent="center" mt={3}>
                            <Button variant="outlined" onClick={handleShowMoreShared}>
                                Show More
                            </Button>
                        </Box>
                    )}
                </>
            ) : (
                <Box textAlign="center" sx={{ mt: 4 }}>
                    <Typography variant="h6">No shared projects found.</Typography>
                </Box>
            )}
        </Container>
    );
}
